import {Component, ComponentCategory, ComponentErrorBits} from '@taopix/taopix-core';
import {PricingEngine} from '@taopix/taopix-core/dist/types/PricingEngine/PricingEngine';
import {PricingModel} from '@taopix/taopix-core/dist/types/PricingEngine/Types';
import APIServer from '@taopix/taopix-core/dist/types/Service/APIServer';
import {ChildComponent, ComponentTree} from '@taopix/taopix-core/dist/types/types';

export interface ComponentServiceResponse {
  error: string;
  errorparam: string;
  data: any[];
}

type SelectedComponentTree = Array<ProductData | ComponentSectionData | undefined | null>;

interface ProductData {
  productquantity: number;
  perfectlyclearapplied: boolean;
}

interface ComponentSectionData {
  sectioncode: ComponentCategory | string;
  children: ComponentData[];
  keywords: KeyWordData[];
}

interface ComponentData {
  componentcode?: ComponentCategory | string;
  localcode: string;
  name: string;
  sectioncode?: ComponentCategory | string;
  quantity?: number;
  keywords: KeyWordData[];
  children: ComponentData[];
  path: string;
  islist: number;
  pricingmodel: number;
  selected: number;
}

interface KeyWordData {
  ref: number;
  code: string;
  name: string;
  description: string;
  flags: string;
  type: string;
  value: string | boolean;
  valuecode: string;
}

type Keyword = any;

export default class ComponentService {
  private server: APIServer = {} as APIServer;
  private pricingEngine: PricingEngine = {} as PricingEngine;

  constructor(server: APIServer, pricingEngine: PricingEngine) {
    this.server = server;
    this.pricingEngine = pricingEngine;
  }

  /**
   * Triggers the selected component save.
   *
   * @param productQuantity Quantity of the product.
   * @param perfectlyClearApplied True if Perfeclty Clear has been applied.
   * @param componentTree Component tree to get the selected components from.
   * @returns Reduced component tree.
   */
  public saveComponentSelection = (
    productQuantity: number,
    perfectlyClearApplied: boolean,
    componentTree: ComponentTree
  ): Promise<ComponentServiceResponse> => {
    return this.server
      .post<any>(
        '/api/project/savecomponents',
        {
          componenttree: JSON.stringify(
            this.getSelectedComponentTreeStructure(
              productQuantity,
              perfectlyClearApplied,
              componentTree
            )
          ),
        },
        {token: 'token', json: true}
      )
      .then((response: any) => {
        return response;
      })
      .catch((error: any) => console.error(error));
  };

  /**
   * Gets a reduced component tree of only the selected components.
   * Selected component can include, components selected by the user, component is flagged as default, or it is the first item in a list.
   *
   * @param productQuantity Quantity of the product.
   * @param perfectlyClearApplied True if Perfeclty Clear has been applied.
   * @param componentTree Component tree to get the selected components from.
   * @returns Reduced component tree.
   */
  private getSelectedComponentTreeStructure = (
    productQuantity: number,
    perfectlyClearApplied: boolean,
    componentTree: ComponentTree
  ): SelectedComponentTree => {
    // Loop on component tree and then filter out null valyes returned my map if a section has been filtered out.
    const components = Object.values(
      componentTree[ComponentCategory.Product]?.children || []
    ) as ChildComponent[];
    const selectedComponentTree: SelectedComponentTree = components
      .flatMap((component: ChildComponent): ComponentSectionData | null => {
        const sectionFilter: string[] = [
          ComponentCategory.TaopixAI,
          ComponentCategory.CalendarCustomisation,
          ComponentCategory.SinglePrint,
          ComponentCategory.SinglePrintOption,
        ];

        if (sectionFilter.indexOf(component.sectioncode) === -1) {
          return this.filterComponents(component);
        }

        return null;
      })
      .filter(
        (selectedComponentTreeItem: any) =>
          selectedComponentTreeItem !== undefined && selectedComponentTreeItem !== null
      );

    // Build the product specific object and put it as the first item in the array.
    selectedComponentTree.unshift({
      productquantity: productQuantity,
      perfectlyclearapplied: perfectlyClearApplied,
    });

    return selectedComponentTree;
  };

  /**
   * Filter components to return the selected components.
   *
   * @param componentCategory Component category to filter.
   * @returns
   */
  private filterComponents = (componentCategory: ChildComponent): ComponentSectionData | null => {
    const {sectioncode} = componentCategory;
    const components: ComponentData[] = this.getComponents(componentCategory, false);
    let filteredComponentCategory: ComponentSectionData = {
      sectioncode: '',
      children: [],
      keywords: [],
    };

    if (components.length > 0) {
      filteredComponentCategory = {sectioncode, children: [], keywords: []};
      filteredComponentCategory.children = components;
      return filteredComponentCategory;
    }

    return null;
  };

  /**
   * Get the selected components in their reduced form.
   *
   * @param componentCategory The component category to get selected components from.
   * @returns Array of selected components.
   */
  private getComponents = (
    componentCategory: ChildComponent,
    isSubComponent: boolean
  ): ComponentData[] => {
    const reducedComponents: ComponentData[] = [];
    const components: ChildComponent[] =
      componentCategory.islist === 1
        ? Object.values(componentCategory.children)
        : [componentCategory];

    // Make sure there are some components.
    if (components.length > 0) {
      let foundComponents: ChildComponent[] = [];
      let listItems: ChildComponent[] = [];

      listItems = components.filter((component: ChildComponent): boolean => {
        return component.islist === 1;
      });

      if (listItems.length > 0) {
        // Check for selected components.
        foundComponents = listItems.filter((component: ChildComponent): boolean => {
          return component.selected === 1 || component.price === '';
        });

        if (foundComponents.length === 0) {
          foundComponents.push(this.getDefaultComponent(listItems));
        }
      }

      // Loop on list item children components.
      foundComponents.forEach((component: ChildComponent) => {
        const {code, text, keywords} = component;
        let {selected} = component;
        let quantity = 0;

        if (component.inheritparentqty === 1 && isSubComponent) {
          quantity = componentCategory.quantity || -1;
        } else {
          quantity =
            component.quantity ||
            Component.getComponentStartAndEndQuantity(component, this.pricingEngine).start;
        }

        if (!component.hasOwnProperty('selected') && component.default === 1) {
          selected = 1;
        }

        const formattedPath: string =
          component.parentpath === ''
            ? component.parentpath
            : this.getFormattedPath(component.parentpath);
        const newComponent: ComponentData = {
          componentcode: code,
          localcode: code || ''.split('.')[1],
          name: text,
          children: [],
          path: formattedPath,
          islist: 1,
          pricingmodel: component.pricingmodel as PricingModel,
          quantity,
          keywords: this.getFormattedKeyWords(Object.values(keywords)),
          selected: selected ? 1 : 0,
        };
        newComponent.children = this.getComponents(component, true);
        reducedComponents.push(newComponent);
      });

      // Get the selected checkboxes.
      const filteredCheckbox: ChildComponent[] = components.filter(
        (component: ChildComponent): boolean => {
          return (
            component.islist === 0 &&
            (component.hasOwnProperty('selected') ? true : component.default === 1) &&
            !Component.hasError(
              component,
              ComponentErrorBits.PageCountRequired +
                ComponentErrorBits.PageCountRequiredSelectionRemoved
            )
          );
        }
      );

      filteredCheckbox.forEach((component: ChildComponent) => {
        const {code, text, keywords} = component;
        let {selected} = component;
        let quantity = 0;

        if (component.inheritparentqty === 1 && isSubComponent) {
          quantity = componentCategory.quantity || -1;
        } else {
          quantity =
            component.quantity ||
            Component.getComponentStartAndEndQuantity(component, this.pricingEngine).start;
        }

        if (!component.hasOwnProperty('selected') && component.default === 1) {
          selected = 1;
        }

        const formattedPath: string =
          component.parentpath === ''
            ? component.parentpath
            : this.getFormattedPath(component.parentpath);
        const newComponent: ComponentData = {
          componentcode: code,
          localcode: code || ''.split('.')[1],
          name: text,
          children: [],
          path: formattedPath,
          islist: 0,
          pricingmodel: component.pricingmodel as PricingModel,
          quantity,
          keywords: this.getFormattedKeyWords(Object.values(keywords)),
          selected: selected ? 1 : 0,
        };
        reducedComponents.push(newComponent);
      });
    }

    return reducedComponents;
  };

  private getFormattedKeyWords = (pKeywords: Keyword[]): KeyWordData[] => {
    const keywords: KeyWordData[] = [];

    pKeywords.forEach((keyword: Keyword): void => {
      keywords.push({
        ref: keyword.ref,
        code: keyword.code,
        name: keyword.name,
        description: keyword.description,
        flags: keyword.flags,
        type: keyword.type,
        value: keyword.value || keyword.defaultvalue || '',
        valuecode: keyword.valuecode || keyword.defaultvalue || '',
      });
    });

    return keywords;
  };

  private getFormattedPath = (pPath: string): string => {
    //return pPath;
    const parts: string[] = pPath.split('.');
    let lineFooter = '';

    if (parts[0] === 'LINEFOOTER') {
      lineFooter = '$' + parts[0] + '\\';
      parts.shift();
    }

    const formattedPath: string = parts
      .map((section: string, index: number): string => {
        return index % 2 === 0 ? '$' + section : section;
      })
      .join('\\');
    return lineFooter.concat(formattedPath);
  };

  /**
   * Loop around all the components of a type and work out if it is the default one.
   *
   * @param pComponents A list of components.
   * @returns The default component or the first one if the default is missing.
   */
  private getDefaultComponent = (pComponents: ChildComponent[]): ChildComponent => {
    return Component.getDefaultComponent(pComponents);
  };
}
